import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Plus, Trash2, Sunrise } from 'lucide-react'
import { postJson } from '../../lib/api.js'
import {
  ANNOUNCEMENT_TOPIC, GOAL_CATEGORIES, NOTE_TOPICS,
  filledGoals, goalNeedsCategory, huddleHasContent,
} from '../../lib/huddle.js'
import Combobox from '../Combobox.jsx'
import {
  Banner, Button, ConfirmDialog, Drawer, DrawerFooter, EmptyState, FIELD_CLASS, Field, IconButton,
  InlineError, Input, Loading, Select, fmtDateText, useToast,
} from '../../kit'

// Manager editor for one branch+day huddle, in the app's right-hand drawer shell
// (same as สร้างคำขอซื้อ / ตัด Stock) so the day's huddle stays readable behind it.
// Full-save: POST /api/huddle upserts the row for (branch_id, huddle_date) and
// publishes it — there is no draft step. Gated by the caller (canManage) AND
// the proxy.
//
// The save is a WHOLE-ROW overwrite, which makes every way of losing typed
// content a data-loss bug rather than an annoyance. Three of them are guarded
// here: a goal that carries only a number is kept (it used to be dropped), a
// blank form landing on a huddle that has content asks first, and closing or
// switching day with unsaved edits asks first.

function AutoTextarea({ value, onChange, rows = 1, className = '', ...rest }) {
  const ref = useRef(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    // border-box height must include the borders, which scrollHeight leaves out
    // — without this the bordered goal field clips its last line by 2px.
    const cs = window.getComputedStyle(el)
    const border = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0)
    el.style.height = `${el.scrollHeight + border}px`
  }, [value])
  return (
    <textarea
      ref={ref}
      rows={rows}
      value={value}
      onChange={onChange}
      className={`resize-none overflow-hidden ${className}`}
      {...rest}
    />
  )
}

const seedGoals = (initial) => (
  Array.isArray(initial?.daily_goals) && initial.daily_goals.length
    ? initial.daily_goals.map((g) => ({ category: g.category || '', text: g.text || '', target: g.target || '' }))
    : [{ category: '', text: '', target: '' }]
)

// ประกาศ is edited as a reserved หัวข้อ inside บันทึกตามหัวข้อ, not as its own
// box — one list, one way to add something. It is split back out to the
// `announcements` column on save, so the read view is unchanged.
const seedNotes = (initial) => {
  const rows = Array.isArray(initial?.notes)
    ? initial.notes.map((n) => ({ topic: n.topic || '', body: n.body || '' }))
    : []
  const ann = (initial?.announcements || '').trim()
  return ann ? [{ topic: ANNOUNCEMENT_TOPIC, body: ann }, ...rows] : rows
}

export default function HuddleEditor({
  initial, branchId, date, branchName, branches = [], ready = true, scopeError = null,
  onScopeChange, onRetry, onSaved, onCancel,
}) {
  const scope = `${branchId}|${date}`
  const [goals, setGoals] = useState(() => seedGoals(initial))
  const [notes, setNotes] = useState(() => seedNotes(initial))
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [confirm, setConfirm] = useState(null)
  const seeded = useRef(scope)
  const toast = useToast()

  // The drawer can switch branch/day without closing. The parent reloads that
  // day's huddle and hands it back as `initial`; `ready` is the parent saying
  // the row on screen really belongs to the scope in the header. Re-seed only
  // then — seeding from the PREVIOUS day's row is how a full-save silently
  // overwrites a huddle that was never opened.
  useEffect(() => {
    if (!ready || seeded.current === scope) return
    seeded.current = scope
    setGoals(seedGoals(initial))
    setNotes(seedNotes(initial))
    setDirty(false)
    setError(null)
  }, [ready, scope, initial])

  const setGoal = (i, patch) => { setDirty(true); setGoals((gs) => gs.map((g, j) => (j === i ? { ...g, ...patch } : g))) }
  const setNote = (i, patch) => { setDirty(true); setNotes((ns) => ns.map((n, j) => (j === i ? { ...n, ...patch } : n))) }
  const addGoal = () => { setDirty(true); setGoals((gs) => [...gs, { category: '', text: '', target: '' }]) }
  const addNote = () => { setDirty(true); setNotes((ns) => [...ns, { topic: '', body: '' }]) }
  const dropGoal = (i) => { setDirty(true); setGoals((gs) => gs.filter((_, j) => j !== i)) }
  const dropNote = (i) => { setDirty(true); setNotes((ns) => ns.filter((_, j) => j !== i)) }

  // Anything typed that is worth warning about before it is thrown away.
  const askDiscard = (title, confirmLabel, run) => {
    if (!dirty) return run()
    setConfirm({
      title,
      message: 'สิ่งที่พิมพ์ไว้และยังไม่ได้บันทึกจะหายไป',
      confirmLabel,
      tone: 'danger',
      onConfirm: () => { setConfirm(null); run() },
    })
  }

  const changeScope = (nextBranch, nextDate) => {
    if (String(nextBranch) === String(branchId) && nextDate === date) return
    askDiscard('เปลี่ยนวัน/สาขาโดยไม่บันทึก?', 'เปลี่ยน', () => onScopeChange?.(String(nextBranch), nextDate))
  }

  // The footer's ยกเลิก. The ✕ / Esc / backdrop go through the kit Drawer's own
  // `dirty` guard; this asks the same question in the same words.
  const close = () => { if (!saving) askDiscard('ทิ้งการแก้ไข?', 'ทิ้งการแก้ไข', () => onCancel?.()) }

  function requestSave() {
    if (goals.some(goalNeedsCategory)) {
      setError('มีเป้าหมายที่กรอกไว้แต่ยังไม่ได้เลือกหมวดหมู่ — เลือกหมวดหมู่ก่อนบันทึก')
      return
    }
    // The category IS the goal; detail and number are optional. Rows with no
    // category at all were never filled in, so they go.
    const cleanGoals = filledGoals(goals).map((g) => ({
      category: g.category,
      text: (g.text || '').trim(),
      target: (g.target || '').trim(),
    }))
    const kept = notes.filter((n) => (n.body || '').trim()).map((n) => ({ topic: (n.topic || '').trim(), body: n.body.trim() }))
    // The POST replaces the whole row. A blank form saved over a huddle that
    // already has content erases it — that has to be a choice, not a slip.
    if (!cleanGoals.length && !kept.length && huddleHasContent(initial)) {
      setConfirm({
        title: 'บันทึกทับด้วยเนื้อหาว่าง?',
        message: 'ฟอร์มนี้ว่างอยู่ แต่ Daily Goal ของวันนี้มีเป้าหมาย/บันทึกอยู่แล้ว การบันทึกจะลบของเดิมทั้งหมด',
        confirmLabel: 'ลบและบันทึก',
        tone: 'danger',
        onConfirm: () => { setConfirm(null); save(cleanGoals, kept) },
      })
      return
    }
    save(cleanGoals, kept)
  }

  async function save(cleanGoals, kept) {
    setSaving(true); setError(null)
    // The reserved ประกาศ topic goes back to its own column (several of them
    // concatenate); everything else stays a note.
    const announcements = kept.filter((n) => n.topic === ANNOUNCEMENT_TOPIC).map((n) => n.body).join('\n\n')
    const cleanNotes = kept.filter((n) => n.topic !== ANNOUNCEMENT_TOPIC)
    try {
      const saved = await postJson('/api/huddle', {
        branch_id: Number(branchId),
        huddle_date: date,
        // No draft step — the owner's call. A huddle is written to be read by
        // the branch that morning, so saving IS publishing. `status` stays in
        // the schema (and the staff read filter) as the safety net for the
        // legacy rows that were saved as drafts before this changed.
        status: 'published',
        daily_goals: cleanGoals,
        announcements,
        notes: cleanNotes,
      })
      setDirty(false)
      toast.success('บันทึกและเผยแพร่แล้ว')
      onSaved?.(saved)
    } catch (e) {
      setError(e.message || 'บันทึกไม่สำเร็จ')
      toast.error('บันทึกไม่สำเร็จ')
      setSaving(false)
    }
  }

  const busy = saving || !ready
  // The pickers stay live through a failed load — changing the day is the way
  // out of a scope that cannot be read.
  const scopeLocked = saving || (!ready && !scopeError)
  const goalOptions = [
    { value: '', label: '— เลือกหมวดหมู่ —' },
    ...GOAL_CATEGORIES.map((c) => ({ value: c.key, label: `${c.icon} ${c.label}` })),
  ]
  const branchOptions = branches.map((b) => ({ value: b.id, label: b.name }))
  const blockReason = scopeError ? 'โหลดไม่สำเร็จ — บันทึกไม่ได้' : !ready ? 'กำลังโหลดข้อมูลของวันนี้…' : ''

  return (
    <>
    <Drawer
      open
      onClose={onCancel}
      icon={Sunrise}
      title={initial?.id ? 'แก้ไข Daily Goal' : 'สร้าง Daily Goal'}
      subtitle={`${fmtDateText(date, { weekday: true })} · สาขา ${branchName || '—'}`}
      busy={saving}
      dirty={dirty}
      discardMessage="สิ่งที่พิมพ์ไว้และยังไม่ได้บันทึกจะหายไป"
      record={initial?.id ? initial : undefined}
      recordTable="daily_huddle"
      footer={(
        <DrawerFooter
          onCancel={close}
          onSave={requestSave}
          saving={saving}
          canSave={!busy}
          status={saving ? '' : blockReason}
          statusTone={scopeError ? 'error' : 'muted'}
          saveLabel="บันทึกและเผยแพร่ให้ทีม"
        />
      )}
    >
        {/* Which day and which branch this huddle IS — editable here, not only
            on the page behind, so a manager can prepare tomorrow (or another
            branch) without closing and losing what they typed. Switching asks
            the parent to reload that scope; the form re-seeds from it. */}
        <div className="mb-5 flex flex-wrap items-end gap-3">
          <Field label="วันที่" className="w-[170px]">
            <Input
              type="date"
              value={date}
              disabled={scopeLocked}
              onChange={(e) => changeScope(branchId, e.target.value || date)}
            />
          </Field>
          <Field label="สาขา" className="min-w-[170px] flex-1">
            {branches.length > 0 ? (
              <Select
                value={String(branchId)}
                disabled={scopeLocked}
                onChange={(v) => changeScope(v, date)}
                options={branchOptions}
              />
            ) : (
              <div className="py-2 text-[13.5px] text-slate-600">{branchName || '—'}</div>
            )}
          </Field>
          {!ready && !scopeError && <div className="pb-2"><Loading variant="inline" label="" /></div>}
        </div>

        {/* Never let the form save into a day whose row could not be read —
            the POST replaces the whole row, so a blind overwrite would erase
            a huddle nobody has seen. */}
        {scopeError && (
          <Banner
            tone="error"
            className="mb-5"
            action={onRetry && <Button size="sm" variant="ghost" onClick={onRetry}>ลองใหม่</Button>}
          >
            โหลด Daily Goal ของวันนี้ไม่สำเร็จ — บันทึกไม่ได้จนกว่าจะโหลดสำเร็จ
          </Banner>
        )}

        <div className="space-y-6">
        {/* Daily Goals */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[13.5px] font-semibold text-slate-800">🎯 Daily Goal</h3>
            <Button size="sm" variant="ghost" icon={Plus} onClick={addGoal}>เพิ่มเป้าหมาย</Button>
          </div>
          {/* Two lines per goal — หมวดหมู่ + ตัวเลข on top, รายละเอียด full width
              underneath, where a sentence has room to breathe. The card border is
              what keeps a detail visibly attached to its own category once a goal
              spans two lines. */}
          <div className="space-y-2.5">
            {goals.map((g, i) => {
              const orphan = goalNeedsCategory(g)
              return (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-2.5 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <Select
                        value={g.category}
                        onChange={(v) => setGoal(i, { category: v })}
                        options={goalOptions}
                        invalid={orphan}
                      />
                    </div>
                    <Input
                      value={g.target}
                      onChange={(e) => setGoal(i, { target: e.target.value })}
                      placeholder="ตัวเลข/เป้า"
                      aria-label="ตัวเลข/เป้า"
                      className="flex-none !w-[110px]"
                    />
                    <IconButton icon={Trash2} label="ลบเป้าหมายนี้" variant="danger-soft" onClick={() => dropGoal(i)} />
                  </div>
                  <AutoTextarea
                    value={g.text}
                    onChange={(e) => setGoal(i, { text: e.target.value })}
                    rows={2}
                    placeholder="รายละเอียดเป้าหมาย (ไม่บังคับ)"
                    className={`${FIELD_CLASS} leading-relaxed`}
                  />
                  {orphan && <InlineError>เลือกหมวดหมู่ของเป้าหมายนี้</InlineError>}
                </div>
              )
            })}
            {goals.length === 0 && <EmptyState variant="compact" title="ยังไม่มีเป้าหมาย" />}
            <div className="text-[12px] text-slate-400">เลือกหมวดหมู่อย่างเดียวก็บันทึกได้ — รายละเอียดและตัวเลขไม่บังคับ</div>
          </div>
        </section>

        {/* Notes per topic */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[13.5px] font-semibold text-slate-800">📝 บันทึกตามหัวข้อ</h3>
            <Button size="sm" variant="ghost" icon={Plus} onClick={addNote}>เพิ่มบันทึก</Button>
          </div>
          <div className="space-y-4">
            {notes.map((n, i) => (
              <div key={i} className="space-y-1.5">
                <div className="flex items-center gap-2">
                  {/* Combobox, not a <datalist>: the native one gives no
                      keyboard highlight and styles itself by OS. freeText lets a
                      หัวข้อ outside NOTE_TOPICS be typed for THIS note only —
                      deliberately no allowCreate, since nothing writes back to
                      the NOTE_TOPICS list and the "＋ เพิ่ม…" row would imply it does.
                      (The kit's Select / SearchSelect cannot take free text, so
                      this stays.) Esc inside it closes its own menu only — it is
                      stopped here so it does not also reach the Drawer. */}
                  <div className="flex-1 min-w-0" onKeyDown={(e) => { if (e.key === 'Escape') e.stopPropagation() }}>
                    <Combobox
                      value={n.topic}
                      onChange={(v) => setNote(i, { topic: v })}
                      options={NOTE_TOPICS}
                      placeholder="หัวข้อ (เลือกหรือพิมพ์เอง)"
                      freeText
                      white
                      dense
                    />
                  </div>
                  <IconButton icon={Trash2} label="ลบบันทึกนี้" variant="danger-soft" onClick={() => dropNote(i)} />
                </div>
                <AutoTextarea
                  value={n.body}
                  onChange={(e) => setNote(i, { body: e.target.value })}
                  placeholder="รายละเอียด…"
                  className={`${FIELD_CLASS} leading-relaxed`}
                />
              </div>
            ))}
            {notes.length === 0 && <EmptyState variant="compact" title="ยังไม่มีบันทึก" hint="กด “เพิ่มบันทึก” แล้วเลือกหัวข้อ (รวมประกาศจากส่วนกลาง) หรือพิมพ์หัวข้อเอง" />}
          </div>
        </section>

          {error && <Banner tone="error">{error}</Banner>}
        </div>
    </Drawer>

      {/* A sibling AFTER the drawer, so it is the top-most overlay (Esc reaches
          it first) and is not confined by the drawer panel's transform. */}
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          tone={confirm.tone}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  )
}
